'use client'

import { useAtom } from 'jotai'
import { useState, useCallback } from 'react';

// lib files
import { ALL_POSSIBLE_VALUES, SORT_BY } from '@/lib/types/search/searchFilters';
import { ContentServiceVideoObject } from '@/lib/types/api/content-service';
import { normalizeVideoData } from '@/lib/helpers/normalize-video-data';
import { PromotedResultType } from '@/lib/content-services/search/promotedResults';
import { searchFiltersAtom } from '@/lib/atoms/search-filters';
import { SearchFiltersObject } from '@/lib/content-services/search/search';
import { VideoAndWebResultCollection, SearchWebResultType } from '@/lib/types/search/results';

// components
import SearchFilters from '@/components/Search/SearchFilters/SearchFilters';
import SearchLandingError from '@/components/Search/SearchLandingError/SearchLandingError';
import SearchWebThumbnail from '@/components/Search/SearchWebThumbnail/SearchWebThumbnail';
import SearchWebPromotedThumbnail from '@/components/Search/SearchWebPromotedThumbnail/SearchWebPromotedThumbnail';
import VideoDetailThumbnail from '@/components/VideoDetailThumbnail/VideoDetailThumbnail';

// svg's
import CloseIcon from '@/public/svg/pbs-close-white.svg';
import ControlSlidersIcon from '@/public/svg/control-sliders.svg';

// styles
import styles from './SearchVideoAndWebResults.module.scss';

interface SearchVideoAndWebResultsProps {
  videoAndWebResults: VideoAndWebResultCollection;
  promotedResults: PromotedResultType[];
  totalResults: number;
  query?: string;
  station: {
    id?: string;
    commonName?: string;
  }
}

const SearchVideoAndWebResults = (props: SearchVideoAndWebResultsProps) => {
  const [searchFilters, setSearchFilters] = useAtom(searchFiltersAtom)
  const [renderFilters, setRenderFilters] = useState(false)
  const { videoAndWebResults, promotedResults, totalResults, query, station } = props;
  const results = videoAndWebResults?.content;
  const hasVideoAndWebResults = results?.length > 0;

  const updateFilter = useCallback((key: keyof SearchFiltersObject, value: ALL_POSSIBLE_VALUES) => {
    setSearchFilters(previousFilters => ({ ...previousFilters, [key]: value }))
  }, [setSearchFilters])

  return (
    <div className={styles.search_video_and_web_results_wrapper}>
      <div className={styles.search_video_and_web_results__count_and_sort}>
        <h2 className={styles.search_video_and_web_results__result_count}>
          {isNaN(totalResults) ? `0` : totalResults} {totalResults === 1 ? `Result` : `Results`}
        </h2>
        <fieldset className={styles.search_video_and_web_results__sort_by}>
          <legend>Sort By:</legend>
          {SORT_BY.map((sortBy, i) => (
            <span key={i}>
              <input
                type='radio'
                name='sort'
                value={sortBy.value}
                checked={searchFilters.rank === sortBy.value}
                onChange={(() => updateFilter('rank', sortBy.value))}
              />
              <label
                htmlFor={sortBy.value}
                onClick={(() => updateFilter('rank', sortBy.value))}
              >{sortBy.label}</label>
            </span>
          ))}
        </fieldset>
      </div>
      <div className={styles.search_video_and_web_results__mobile_controls}>
        <button className={styles.search_video_and_web_results__mobile_filters_button} onClick={() => setRenderFilters(true)}>
          Filter
          <ControlSlidersIcon />
        </button>

        <select
          className={styles.search_video_and_web_results__mobile_sort_by}
          name='sort-by'
          onChange={(e) => updateFilter('rank', e.target.value as ALL_POSSIBLE_VALUES)}
          value={searchFilters.rank}
        >
          {SORT_BY.map((sortBy, i) => {
            return (
              <option value={sortBy.value} key={i}>Sort By: {sortBy.label}</option>
            )
          })}
        </select>
      </div>
      { hasVideoAndWebResults ? (
        <div className={styles.search_video_and_web_results}>

          {promotedResults && promotedResults.map((result, index) => {
            return (
              <SearchWebPromotedThumbnail
                key={index}
                data={result}
              />
            )
          })}

          {results.map((result, index) => {
            if (result.item_type === 'video') {
              const processedVideo = normalizeVideoData(result as ContentServiceVideoObject, "shortEpOnly", false);
              return (
                <VideoDetailThumbnail
                  key={index}
                  video={processedVideo}
                  displayShowName={true}
                />
              );
            } else if (result.item_type === 'remote_asset') {
              return (
                <SearchWebThumbnail
                  key={index}
                  data={result as SearchWebResultType}
                />
              );
            }
          })}
        </div>
        ) : (
          <SearchLandingError query={query} />
        )
      }
      { renderFilters &&
        <div className={styles.search_video_and_web_results__filters}>
          <SearchFilters station={station} />
          <CloseIcon
            className={styles.search_video_and_web_results__filters_close_icon}
            onClick={() => setRenderFilters(false)}
          />
        </div>
      }
    </div>
  );
};

export default SearchVideoAndWebResults;
